import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { analytics } from '@/utils/analytics';

type PaymentOutcome = 'success' | 'canceled';

function PaymentReturnPage({ outcome }: { outcome: PaymentOutcome }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fired = React.useRef(false);
  const purchase = searchParams.get('purchase');
  const purchaseType = purchase === 'subscription' ? 'subscription' : 'credit';
  const checkoutState = `${purchaseType}-${outcome}`;
  const destination = `/credits?checkout=${checkoutState}`;
  const sessionId = searchParams.get('session_id') ?? 'unknown';

  React.useEffect(() => {
    if (!fired.current && outcome === 'success' && purchaseType === 'credit') {
      fired.current = true;
      analytics.purchaseCompleted(sessionId, 0, 0);
    }
  }, [outcome, purchaseType, sessionId]);

  React.useEffect(() => {
    const timer = setTimeout(() => navigate(destination, { replace: true }), 2000);
    return () => clearTimeout(timer);
  }, [destination, navigate]);

  const succeeded = outcome === 'success';

  return (
    <div className='flex size-full items-center justify-center bg-white p-6 text-center'>
      <section className='w-full max-w-lg rounded-20 border border-purple-150 bg-purple-25 p-8'>
        <p className='font-sans text-12 font-bold uppercase tracking-wide text-purple-680'>
          Billing
        </p>
        <h1 className='mt-2 font-sans text-24 font-bold text-brand-800 md:text-32'>
          {succeeded ? 'Payment successful' : 'Checkout canceled'}
        </h1>
        <p className='mt-3 font-sans text-15 leading-24 text-gray-650'>
          {succeeded
            ? 'We received your payment and are returning you to billing.'
            : 'Nothing was charged. You can review the options and try again whenever you are ready.'}
        </p>
        <button
          type='button'
          onClick={() => navigate(destination, { replace: true })}
          className='mt-6 min-h-11 rounded-30 bg-brand-800 px-6 font-sans text-14 font-bold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-800'
        >
          Return to billing
        </button>
      </section>
    </div>
  );
}

/** Post-payment landing page. Records completion and returns to the unified billing screen. */
export function PaymentSuccessPage() {
  return <PaymentReturnPage outcome='success' />;
}

/** Safe return page when a customer backs out of provider checkout. */
export function PaymentCancelPage() {
  return <PaymentReturnPage outcome='canceled' />;
}
